import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Crown,
  Zap,
  Bell,
  Palette,
  Globe,
  Lock,
  Check,
  ArrowRight,
} from "lucide-react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import {
  getUserSubscription,
  getSubscriptionTier,
} from "../../services/subscriptionService";
import { useToast } from "../../components/Toast";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

const SettingsPage = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const functions = getFunctions();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [theme, setTheme] = useState("Light");
  const [language, setLanguage] = useState("English");
  const [privacyLevel, setPrivacyLevel] = useState("Public");
  const [isPro, setIsPro] = useState(false);
  const [planName, setPlanName] = useState("Starter");
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleBackToHome = () => {
    // This would typically use your router's navigation
    // For example: router.push('/') or navigate('/')
    // window.history.back();
    navigate("/home");
  };

  const handleSaveSettings = () => {
    // Handle saving settings logic here
    alert("Settings saved successfully!");
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);

    // Open the window IMMEDIATELY during the click event (before async call)
    // This prevents popup blockers since it's clearly user-initiated
    // const popup = window.open("about:blank", "_blank");

    try {
      const createPortalSessionFn = httpsCallable(
        functions,
        "createPortalSession"
      );
      const result = await createPortalSessionFn();
      const data = result.data;

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Failed to get portal URL");
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      addToast(
        err?.message ||
          "Failed to open subscription management. Please try again.",
        "error"
      );
    } finally {
      setLoadingPortal(false);
    }
  };

  // Refresh subscription status when page becomes visible (e.g., returning from Stripe portal)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh subscription status
        const refreshStatus = async () => {
          try {
            const user = auth.currentUser;
            if (user) {
              const plan = await getSubscriptionTier(user.uid);
              const subStatus = await getUserSubscription(user.uid);
              setIsPro(subStatus === "active");
              setPlanName(plan || "Starter");
            }
          } catch (error) {
            console.error("Error refreshing subscription status:", error);
          }
        };
        refreshStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (!authUser) {
          setIsPro(false);
          setPlanName("Starter");
          return;
        }

        // Get subscription plan name
        let plan = "Starter";
        let isProUser = false;
        try {
          plan = await getSubscriptionTier(authUser.uid);
          const subStatus = await getUserSubscription(authUser.uid);
          isProUser = subStatus === "active";
        } catch (error) {
          console.error("Error fetching subscription info:", error);
        }

        setIsPro(isProUser);
        setPlanName(plan || "Starter");
      } catch (error) {
        console.error("Error setting up subscription state:", error);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-x-hidden">
      {/* Background Gradients */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 opacity-20 blur-3xl"></div>
      <div className="absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-20 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 opacity-15 blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 opacity-15 blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <button
            onClick={handleBackToHome}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-4 group"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Home</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">
            Customize your experience and manage your subscription
          </p>
        </div>

        {/* Subscription Management Card */}
        {!isPro ? (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2 flex items-center">
                    <Crown className="h-6 w-6 mr-2 text-yellow-300" />
                    <span>Subscription Management</span>
                  </h2>
                  <p className="text-purple-200">
                    Current Plan:{" "}
                    <span className="font-bold text-yellow-300">
                      {planName}
                    </span>
                  </p>
                </div>
                <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">
                  {planName} Plan
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Current Features */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Zap className="h-5 w-5 mr-2 text-blue-600" />
                    Current Features
                  </h3>
                  <ul className="space-y-2">
                    <li className="text-blue-700">• Up to 2 quiz slots</li>
                    <li className="text-blue-700">• 3 AI requests per day</li>
                    <li className="text-blue-700">• Basic analytics</li>
                    <li className="text-blue-700">• Standard support</li>
                  </ul>
                </div>

                {/* Upgrade Section */}
                <div className="flex flex-col justify-center">
                  <button
                    onClick={() => navigate("/payment")}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-105 flex items-center justify-center mb-3"
                  >
                    <Crown className="h-5 w-5 mr-2" />
                    Upgrade to Premium
                  </button>
                  <p className="text-gray-600 text-center text-sm">
                    Unlock unlimited features with premium
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 border border-gray-200">
            <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 text-white relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              ></div>
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <Crown className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Pro Plan Active</h2>
                    <p className="text-indigo-100 text-sm">
                      Current Plan:{" "}
                      <span className="font-bold text-yellow-300">
                        {planName}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                  <span className="text-sm font-semibold">Active</span>
                </div>
              </div>
              <div className="relative grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">12 Quiz Slots</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">25 AI Requests/day</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">Full Vault Access</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">Advanced Analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">Email Prizes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">Priority Support</span>
                </div>
              </div>
              <div className="relative pt-4 border-t border-white/20">
                <button
                  onClick={handleManageSubscription}
                  disabled={loadingPortal}
                  className="bg-white text-indigo-600 font-semibold py-2 px-4 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>
                    {loadingPortal ? "Loading..." : "Manage Subscription"}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* General Settings Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <svg
                className="h-6 w-6 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              General Settings
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Bell className="h-5 w-5 mr-3 text-blue-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Email Notifications
                  </h3>
                  <p className="text-sm text-gray-600">
                    Receive updates about your forms and account
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEmailNotifications(!emailNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailNotifications ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    emailNotifications ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Theme */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Palette className="h-5 w-5 mr-3 text-purple-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">Theme</h3>
                  <p className="text-sm text-gray-600">
                    Choose your preferred color scheme
                  </p>
                </div>
              </div>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="Light">Light</option>
                <option value="Dark">Dark</option>
                <option value="Auto">Auto</option>
              </select>
            </div>

            {/* Language */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Globe className="h-5 w-5 mr-3 text-green-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">Language</h3>
                  <p className="text-sm text-gray-600">
                    Select your preferred language
                  </p>
                </div>
              </div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="English">English</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="German">German</option>
              </select>
            </div>

            {/* Privacy Level */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Lock className="h-5 w-5 mr-3 text-red-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">Privacy Level</h3>
                  <p className="text-sm text-gray-600">
                    Control who can see your forms
                  </p>
                </div>
              </div>
              <select
                value={privacyLevel}
                onChange={(e) => setPrivacyLevel(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Public">Public</option>
                <option value="Private">Private</option>
                <option value="Friends Only">Friends Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Save Settings Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105 flex items-center"
          >
            <Check className="h-5 w-5 mr-2" />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
